import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SnotifyService } from 'ng-snotify';
import { RecommendationsService } from 'src/app/core/services/recommendation.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { amazonProductLink } from 'src/app/core/constants/amazon-link.constant';
import { BulkJobHistory, BulkJobSnapshot } from '../bulk-history.component';
import Swal from 'sweetalert2';
import { BulkPriceJobStatus } from 'src/app/core/infrastructure/enums/meta-data-field.enum';

@Component({
  selector: 'app-bulk-history-detail-modal',
  templateUrl: './bulk-history-detail-modal.component.html',
  styleUrls: ['./bulk-history-detail-modal.component.scss'],
})
export class BulkHistoryDetailModalComponent implements OnInit, OnDestroy {
  @Input() job!: BulkJobHistory;
  @Input() isReverted = false;

  public snapshots: BulkJobSnapshot[] = [];
  public total = 0;
  public totalCount = 0;
  public appliedCount = 0;
  public pendingCount = 0;
  public failedCount = 0;
  public doneCount = 0;
  public invalidCount = 0;
  public isLoading = false;
  public isReverting = false;
  public isBeingReverted = false;
  public revertJobBatchId?: string;

  // Kendo grid pagination
  public pageSize = 10;
  public skip = 0;

  public detailSearch = '';
  public statusOptions: Array<{ text: string; value: string }> = [
    { text: `All (${this.totalCount})`, value: BulkPriceJobStatus.ALL },
    { text: `Working (0)`, value: BulkPriceJobStatus.PENDING },
    { text: `Done (${this.doneCount})`, value: BulkPriceJobStatus.DONE },
    { text: `Failed (${this.failedCount})`, value: BulkPriceJobStatus.FAILED },
    { text: `Invalid (${this.invalidCount})`, value: BulkPriceJobStatus.INVALID },
  ];
  public selectedStatus = BulkPriceJobStatus.ALL as string;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private recommendationService: RecommendationsService,
    private snotifyService: SnotifyService,
    private companyService: CompanyService,
  ) {}

  ngOnInit(): void {
    this.loadData();
    
    this.searchSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((searchValue) => {
      this.detailSearch = searchValue;
      this.skip = 0;
      this.loadSnapshots(this.pageSize, this.skip);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public pageChange(event: { skip: number; take: number }): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.loadSnapshots(this.pageSize, this.skip);
  }

  public onDetailSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  public clearSearch(): void {
    this.detailSearch = '';
    this.searchSubject.next('');
  }

  public onStatusChange(value: string): void {
    this.selectedStatus = value;
    this.skip = 0;
    this.loadSnapshots(this.pageSize, this.skip);
  }

  public onClose(): void {
    this.activeModal.dismiss();
  }

  public openRelatedJob(jobBatchId: string | undefined): void {
    if (!jobBatchId) return;
    this.activeModal.close({ navigateTo: jobBatchId });
  }

  public getSkuLink(asin: string): string {
    const marketplaceId = this.companyService.currentCompany()?.marketplaceId ?? '';
    return amazonProductLink(asin, marketplaceId);
  }

  public getTrendPercent(snap: BulkJobSnapshot): number | null {
    if (!snap.priceAfter || !snap.priceBefore) return null;
    const before = snap.priceBefore.inboundPrice;
    const after = snap.priceAfter.inboundPrice;
    if (!before || before === 0) return null;
    return ((after - before) / before) * 100;
  }

  public getEffectiveMin(snap: BulkJobSnapshot): number | null | undefined {
    return snap.priceRequested?.minPrice != null ? snap.priceRequested.minPrice : snap.priceBefore?.minPrice;
  }

  public getEffectiveMax(snap: BulkJobSnapshot): number | null | undefined {
    return snap.priceRequested?.maxPrice != null ? snap.priceRequested.maxPrice : snap.priceBefore?.maxPrice;
  }

  public canRevert(): boolean {
    return (
      (!this.job.isRevert && !this.isReverted && !this.isBeingReverted && !this.job.pendingRevert) ||
      this.canRetryRevert()
    );
  }

  public canRetryRevert(): boolean {
    return this.job.isRevert && this.job.status === 'FAILED' && !!this.job.originalJobBatchId;
  }

  public getRevertButtonLabel(): string {
    if (this.canRetryRevert()) return 'Retry Revert';
    return this.job.status === 'FAILED' ? 'Revert Applied SKUs' : `Revert ${this.job.totalCount} SKUs`;
  }

  public onRevert(): void {
    const targetJobBatchId = this.canRetryRevert() ? this.job.originalJobBatchId : this.job.jobBatchId;
    if (!targetJobBatchId) return;

    const isRetryRevert = this.canRetryRevert();
    const isDeferred = this.job.status === 'IN_PROGRESS' || this.job.status === 'PENDING';
    const isFailed = this.job.status === 'FAILED';
    Swal.fire({
      title: isRetryRevert ? 'Retry Revert?' : 'Revert Prices?',
      html: isRetryRevert
        ? `<p>This will run a new revert job from the original price update.</p>
           <p><strong>${this.job.totalCount} SKU${this.job.totalCount === 1 ? '' : 's'}</strong> will be retried.</p>`
        : isFailed
        ? `<p>This failed job may have changed some prices before it stopped. This will queue a revert for any SKU that was successfully applied.</p>
           <p><strong>${this.job.doneCount || this.job.totalCount} SKU${(this.job.doneCount || this.job.totalCount) === 1 ? '' : 's'}</strong> may be affected.</p>`
        : isDeferred
          ? `<p>This will <strong>schedule</strong> a revert — prices will be restored automatically once the job finishes applying.</p>
             <p><strong>${this.job.totalCount} SKU${this.job.totalCount === 1 ? '' : 's'}</strong> will be affected.</p>`
          : `<p>This will restore the prices for all SKUs back to what they were before.</p>
             <p><strong>${this.job.totalCount} SKU${this.job.totalCount === 1 ? '' : 's'}</strong> will be affected.</p>`,
      type: 'warning',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: isRetryRevert
        ? `Retry revert for ${this.job.totalCount} SKU${this.job.totalCount === 1 ? '' : 's'}`
        : isDeferred
        ? `Schedule revert for ${this.job.totalCount} SKU${this.job.totalCount === 1 ? '' : 's'}`
        : `Yes, revert ${isFailed ? 'applied' : this.job.totalCount} SKU${!isFailed && this.job.totalCount === 1 ? '' : 's'}`,
      confirmButtonClass: 'btn btn-danger',
      cancelButtonClass: 'btn btn-secondary',
      allowOutsideClick: false,
      onBeforeOpen: (popup: HTMLElement) => {
        const container = popup.closest('.swal2-container') as HTMLElement;
        if (container) container.style.zIndex = '99999';
      },
    }).then((result) => {
      if (result.dismiss) return;

      this.isReverting = true;
      const notification = new Subject<any>();
      this.snotifyService.async('Queuing revert job...', notification);

      this.recommendationService
        .revertBulkJob(targetJobBatchId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res?.scheduled) {
              notification.next({ body: 'Revert scheduled — will apply automatically once the job completes.', config: { type: 'info', timeout: 8000 } });
              notification.complete();
              this.isReverting = false;
              this.activeModal.close('revert-scheduled');
            } else {
              notification.next({
                body: isRetryRevert
                  ? 'Revert retry has been queued. Prices will be restored shortly.'
                  : 'Revert job has been queued. Prices will be restored shortly.',
                config: { type: 'success', timeout: 10000 },
              });
              notification.complete();
              this.isReverting = false;
              this.activeModal.close('reverted');
            }
          },
          error: () => {
            notification.error({ body: 'Failed to queue revert', config: { type: 'error' } });
            notification.complete();
            this.isReverting = false;
          },
        });
    });
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      snapshots: this.recommendationService.getBulkJobSnapshots(this.job.jobBatchId, this.pageSize, this.skip, this.detailSearch, this.selectedStatus),
      freshJob: this.recommendationService.getBulkJobById(this.job.jobBatchId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ snapshots, freshJob }) => {
          this.snapshots = snapshots?.data ?? snapshots ?? [];
          this.total = snapshots?.total ?? this.job.totalCount;
          this.appliedCount = freshJob?.appliedCount;
          this.totalCount = freshJob?.totalCount;
          this.failedCount = freshJob?.failedCount;
          this.doneCount = freshJob?.doneCount;
          this.invalidCount = freshJob?.invalidCount;
          this.pendingCount = freshJob?.pendingCount;
          this.updateStatusOptions();
          // Use server-authoritative flags — independent of the parent's revertJobs list.
          this.isReverted = !!freshJob?.hasBeenReverted;
          this.isBeingReverted = !!freshJob?.isBeingReverted;
          this.revertJobBatchId = freshJob?.revertJobBatchId;
          this.isLoading = false;
        },
        error: () => {
          this.snotifyService.error('Failed to load snapshot details');
          this.isLoading = false;
        },
      });
  }

  private loadSnapshots(limit: number, offset: number): void {
    this.isLoading = true;
    this.recommendationService
      .getBulkJobSnapshots(this.job.jobBatchId, limit, offset, this.detailSearch, this.selectedStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.snapshots = res?.data ?? [];
          this.total = res?.total ?? this.job.totalCount;
          this.updateStatusOptions();
          this.isLoading = false;
        },
        error: () => {
          this.snotifyService.error('Failed to load snapshot details');
          this.isLoading = false;
        },
      });
  } 

  private updateStatusOptions(): void {
    // If you prefer the 'All' count to reflect the original job size rather than the filtered result, 
    // you can use this.job.totalCount here instead of this.total.
    this.statusOptions = [
      { text: `All (${this.totalCount})`, value: BulkPriceJobStatus.ALL },
      { text: `Working (${this.appliedCount + this.pendingCount})`, value: BulkPriceJobStatus.PENDING },
      { text: `Done (${this.doneCount})`, value: BulkPriceJobStatus.DONE },
      { text: `Failed (${this.failedCount})`, value: BulkPriceJobStatus.FAILED },
      { text: `Invalid (${this.invalidCount})`, value: BulkPriceJobStatus.INVALID },
    ];
    // Create a new array reference so Kendo dropdown detects the change
    this.statusOptions = [...this.statusOptions];
  }
}
